import { Component } from '@verndale/core';
import { tns } from 'tiny-slider/src/tiny-slider';
import { gsap, Linear } from 'gsap';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      $el: this.el,
      $carousel: this.el.querySelector('.content-card-slider-carousel'),
      $initialSlides: this.el.querySelectorAll('.content-card-slider__slide'),
      $navigationWrapper: this.el.querySelector(
        '.content-card-slider-carousel-navigation__wrapper'
      ),
      $navigationDotsContainer: this.el.querySelector(
        '.content-card-slider-carousel-navigation--dots'
      ),
      $navigationBtns: this.el.querySelectorAll(
        '.content-card-slider-carousel-navigation--dots button'
      ),
      $btnGoToLast: this.el.querySelector(
        '.content-card-slider-carousel-navigation--present'
      ),
      $btnExpandCollapse: this.el.querySelectorAll(
        '.content-card__content-btn-expand-collapse'
      )
    };
    this.carousel = null;
    this.timeline = null;
    this.autoplayTimeout = 0;
    this.isAutoplay = false;
    this.slideActiveClass = 'tns-slide-active';
    this.navigationBtnActiveClass =
      'content-card-slider-carousel-navigation__btn--active';
    this.goTo = {
      prev: 'prev',
      next: 'next',
      last: 'last'
    };

    this.isMasthead = this.dom.$el.dataset.module === 'mastheadSlider';

    this.init();
    this.populateNavigationDots();
    this.initCarousel();
  } // setupDefaults

  addListeners() {
    const { $btnGoToLast, $btnExpandCollapse } = this.dom;

    $btnGoToLast?.addEventListener('click', this.goToLastSlide.bind(this));
    $btnExpandCollapse.forEach(trigger =>
      trigger.addEventListener('click', this.onClickExpandCollapse.bind(this))
    );
    window.addEventListener('resize', this.updateHeigthOnSlider.bind(this));
  } // addListeners

  init() {
    const { $el, $navigationWrapper, $initialSlides } = this.dom;

    this.autoplayTimeout = parseFloat($el?.dataset?.autoplayTimeout) || 0;

    if (this.el.dataset.autoplay) {
      this.isAutoplay =
        JSON.parse(this.el.dataset.autoplay) === true
          ? this.autoplayTimeout
          : 0;
    } else {
      this.isAutoplay = this.autoplayTimeout >= 1;
    }

    if ($navigationWrapper && $initialSlides && $initialSlides.length > 1)
      $navigationWrapper.style.display = 'block';

    if (
      ('ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0) &&
      this.isAutoplay === false
    ) {
      this.isAutoplay = true;
      this.autoplayTimeout = 0;
    }
  } // init

  populateNavigationDots() {
    const { $el, $initialSlides, $navigationDotsContainer } = this.dom;

    for (const $slide of $initialSlides) {
      const $btn = document.createElement('button');
      const $span = document.createElement('span');

      $btn.appendChild($span);
      if ($navigationDotsContainer) $navigationDotsContainer.appendChild($btn);
    }

    this.dom.$navigationBtns = $el.querySelectorAll(
      '.content-card-slider-carousel-navigation--dots button'
    );
    this.dom.$navigationBtns.forEach(trigger =>
      trigger.addEventListener('click', this.onClickNavigationBtn.bind(this))
    );
  } // populateNavigationDots

  initCarousel() {
    const { $carousel, $navigationDotsContainer } = this.dom;

    const loop = this.isMasthead && this.isAutoplay ? this.isMasthead : false;

    this.carousel = tns({
      container: $carousel,
      nav: true,
      items: 1,
      loop,
      center: true,
      preventScrollOnTouch: 'auto',
      gutter: 40,
      autoHeight: true,
      controls: false,
      navContainer: $navigationDotsContainer,
      lazyload: false,
      autoplay: this.isAutoplay,
      autoplayButtonOutput: false,
      autoplayHoverPause: false,
      autoplayTimeout: this.autoplayTimeout * 1000,
      responsive: {
        1024: {
          autoHeight: false,
          gutter: 60
        }
      },
      onInit: this.onInitCarousel.bind(this)
    });

    if (this.carousel)
      this.carousel.events.on('indexChanged', this.onIndexChanged.bind(this));
  } // initCarousel

  updateHeigthOnSlider() {
    this.carousel.updateSliderHeight()
  }

  onInitCarousel() {
    this.addSlidesListener();
    this.animateProgress();
  } // onInitCarousel

  onIndexChanged() {
    if (this.timeline) this.timeline.restart();
  } // onIndexChanged

  goToLastSlide(e) {
    e?.preventDefault();

    if (this.carousel) this.carousel.goTo(this.goTo.last);
  } // goToLastSlide

  addSlidesListener() {
    const { $el, $carousel } = this.dom;
    const domSlides = $el.querySelectorAll(
      '.content-card-slider__slide .content-card'
    );

    domSlides.forEach(trigger =>
      trigger.addEventListener('click', this.onClickSlide.bind(this))
    );
    $carousel?.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    $carousel?.addEventListener('mouseleave', this.onMouseLeave.bind(this));
  } // addSlidesListener

  onClickSlide(e) {
    e?.preventDefault();
    if (!this.carousel) return;

    const { $el } = this.dom;
    const currentTarget = e?.currentTarget;
    const slide = currentTarget.closest('.content-card-slider__slide');
    if (slide && slide.classList.contains(this.slideActiveClass)) {
      return;
    }

    const nextElement = slide?.nextElementSibling;
    if (nextElement && nextElement.classList.contains(this.slideActiveClass)) {
      this.carousel.goTo(this.goTo.prev);
    } else {
      this.carousel.goTo(this.goTo.next);
    }

    const btnActive = $el.querySelector(
      '.content-card-slider-carousel-navigation--dots .tns-nav-active'
    );
    if (btnActive) btnActive.classList.add(this.navigationBtnActiveClass);
  } // onClickSlide

  onMouseEnter() {
    if (!this.carousel || !this.isAutoplay) return;

    this.carousel.pause();
    if (this.timeline) this.timeline.pause();
  } // onMouseEnter

  onMouseLeave() {
    if (!this.carousel || !this.isAutoplay) return;

    this.carousel.play();
    if (this.timeline) this.timeline.restart();
    this.removeNavigationBtnActiveClass();
  } // onMouseLeave

  animateProgress() {
    const { $el } = this.dom;
    const $progressIndicator = $el.querySelectorAll(
      '.content-card-slider-carousel-navigation--dots button span'
    );

    this.timeline = gsap.timeline();

    if (!$progressIndicator) return;
    this.timeline.to($progressIndicator, {
      width: '100%',
      duration: this.autoplayTimeout,
      ease: Linear.easeNone
    });
  } // animateProgress

  onClickExpandCollapse(e) {
    e?.preventDefault();
    if (!this.carousel) return;

    setTimeout(() => {
      this.carousel.updateSliderHeight();
    }, 20);

    let counter = 0;
    const intervalId = window.setInterval(() => {
      this.carousel.updateSliderHeight();
      counter++;
      if (counter >= 4) clearInterval(intervalId);
    }, 100);
  } // onClickExpandCollapse

  onClickNavigationBtn(e) {
    e?.preventDefault();

    const currentTarget = e?.currentTarget;
    if (currentTarget?.classList?.contains(this.navigationBtnActiveClass)) {
      return;
    }

    this.removeNavigationBtnActiveClass();
    currentTarget.classList.add(this.navigationBtnActiveClass);
  } // onClickNavigationBtn

  removeNavigationBtnActiveClass() {
    const { $navigationBtns } = this.dom;

    for (const $btn of $navigationBtns) {
      $btn.classList.remove(this.navigationBtnActiveClass);
    }
  } // removeNavigationBtnActiveClass
}

export default Module;
